<template>
  <div v-if="automation" v-bind:class="['show-automation']" class="flex-col gap-y-md">
    <dd-header-card header="Automation instellingen">
      <AutomationDescriptionComponent v-bind:key="automationKey" v-bind:value="automation"/>
      <dd-card-stacked-list>
        <dd-card-list-row>
          <dd-card-list-item title="Omschrijving">
            {{ automation.description }}
          </dd-card-list-item>
          <dd-card-list-item v-bind:title="frequencyHeaderDescription">
            <div>
              <p v-if="getRunInterval === RunIntervalEnum.NEVER">
                Automation staat niet aan.
              </p>
              <p v-else-if="getRunInterval === RunIntervalEnum.ONCE">
                Automation wordt <strong>eenmalig</strong> uitgevoerd wanneer deze <strong>handmatig</strong> gestart wordt
              </p>
              <p v-else>
                Automation <strong>{{ getRunIntervalDescription }}</strong> uitvoeren.
                De volgende uitvoering start op <strong>{{ getNextSendDate }}</strong>
              </p>
            </div>
          </dd-card-list-item>
        </dd-card-list-row>
        <dd-card-list-row>
          <dd-card-list-item title="Resulaten opnieuw genereren">
            {{ automation.is_recurring ? 'Ja' : 'Nee' }}
          </dd-card-list-item>
          <dd-card-list-item v-if="automation.id" title="Laatst uitgevoerd op">
            {{ lastRanAt }}
          </dd-card-list-item>
        </dd-card-list-row>
        <dd-card-list-row>
          <dd-card-list-item title="Resultaten tijdelijk niet opnieuw genereren">
            {{ automation.temporary_exclude_recurring ? 'Ja' : 'Nee' }}
          </dd-card-list-item>
          <dd-card-list-item v-if="automation.temporary_exclude_recurring" title="Resultaten niet opnieuw genereren voor">
            {{ getIgnoreRecurringDurationDisplay }}
          </dd-card-list-item>
        </dd-card-list-row>
      </dd-card-stacked-list>
    </dd-header-card>
  </div>
</template>

<script>
import {AutomationTypeEnum} from '../../enums'
import AutomationDescriptionComponent from '../DescriptionComponent.vue'
import {mapGetters} from 'vuex'
import {RunIntervalEnum} from "~/pages/automation/enums";
import moment from "moment/moment";
import {DateFormatEnum} from "~/enums";

export default {
  name: 'ShowAutomationComponent',
  components: {
    AutomationDescriptionComponent,
  },
  props: {
    isGlobal: {
      type: Boolean,
      required: false,
    },
    isCreating: {
      type: Boolean,
      default: false
    },
    automation: {
      type: Object,
      required: true,
    },
  },
  computed: {
    RunIntervalEnum() {
      return RunIntervalEnum
    },
    ...mapGetters(`$_automation_show`, {
      automationKey: 'automationKey',
      lastRanAt: 'lastRanAt',
      hasRan: 'hasRan',
      nextSendDate: 'nextSendDate',
      runIntervalDescription: 'runIntervalDescription',
      runInterval: 'runInterval',
    }),
    automationKey() {
      if (this.isGlobal) {
        return 'global-automation-configuration-' + this.automation.id
      }

      return 'automation-' + this.automation.id
    },
    header() {
      return this.isCreating ? 'Automation instellingen' : this.automation.title;
    },
    getRunInterval() {
      if (this.automation.id) {
        return this.runInterval
      }

      return this.automation.run_interval
    },
    frequencyHeaderDescription() {
      let header = 'Hoe vaak draait de '

      if (this.automation.type === AutomationTypeEnum.CAMPAIGN) {
        header += 'campagne'
      } else if (this.automation.type === AutomationTypeEnum.SELECTION) {
        header += 'selectie'
      } else if (this.automation.type === AutomationTypeEnum.DATA_ENRICHMENT) {
        header += 'data verrijking'
      }

      return header
    },
    getRunIntervalDescription()
    {
      if (this.automation.id) {
        return this.nextSendDate
      }

      // overview page in the create-flow
      return this.getTranslatedRunInterval()
    },
    getNextSendDate()
    {
      if (this.automation.id) {
        return this.runIntervalDescription
      }

      // overview page in the create-flow
      return this.getNextSendDateFunction()
    },
    getIgnoreRecurringDurationDisplay() {
      if (!this.automation.temporary_exclude_recurring) {
        return ''
      }

      let display = ''

      if (this.automation.temporary_exclude_recurring_duration.years !== undefined) {
        display += this.automation.temporary_exclude_recurring_duration.years + ' jaar '
      }

      if (this.automation.temporary_exclude_recurring_duration.months !== undefined) {
        display += this.automation.temporary_exclude_recurring_duration.months + ' maand '
      }

      if (this.automation.temporary_exclude_recurring_duration.weeks !== undefined) {
        display += this.automation.temporary_exclude_recurring_duration.weeks + ' week '
      }

      if (this.automation.temporary_exclude_recurring_duration.days !== undefined) {
        display += this.automation.temporary_exclude_recurring_duration.days + ' dag'
      }

      return display
    }
  },
  methods: {
    getTranslatedRunInterval() {
      if (this.automation.run_interval === RunIntervalEnum.DAILY) {
        return 'Dagelijks'
      } else if (this.automation.run_interval === RunIntervalEnum.ONCE) {
        return 'Eenmalig'
      } else if (this.automation.run_interval === RunIntervalEnum.WEEKLY) {
        return 'Wekelijks'
      } else if (this.automation.run_interval === RunIntervalEnum.MONTHLY) {
        return 'Maandelijks'
      } else if (this.automation.run_interval === RunIntervalEnum.QUARTERLY) {
        return 'Elk kwartaal'
      } else if (this.automation.run_interval === RunIntervalEnum.YEARLY) {
        return 'Jaarlijks'
      }

      return 'Onbekend'
    },
    getNextSendDateFunction() {
      switch (this.getRunInterval) {
        case RunIntervalEnum.NEVER:
          return ", niet deze staat uit.";
        case RunIntervalEnum.DAILY:
          return moment()
              .add(1, "day")
              .format(DateFormatEnum.NEW_DISPLAY_DATE);
        case RunIntervalEnum.WEEKLY:
          return moment()
              .startOf("isoWeek")
              .add(1, "week")
              .format(DateFormatEnum.NEW_DISPLAY_DATE);
        case RunIntervalEnum.MONTHLY:
          return moment()
              .startOf("month")
              .add(1, "month")
              .format(DateFormatEnum.NEW_DISPLAY_DATE);
        case RunIntervalEnum.QUARTERLY:
          return moment()
              .startOf("month")
              .add(3, "month")
              .format(DateFormatEnum.NEW_DISPLAY_DATE);
        case RunIntervalEnum.YEARLY:
          return moment()
              .startOf("month")
              .add(1, "year")
              .format(DateFormatEnum.NEW_DISPLAY_DATE);
        default:
          return "";
      }

      return 'Onbekend'
    }
  }
}
</script>

<style>
.show-automation .ant-form-item {
  margin-bottom: 15px !important;
}

.show-automation .ant-form-item-control, .show-automation .ant-form-item-label {
  line-height: 1.5 !important;
}
</style>
